import React, { ReactNode } from 'react'

import { LntParagraph } from '@app/components'
import { RendererWrapper } from '@app/components/renderers'

import { isObject } from '@app/typeGuards'
import { getStylesClassName } from '@app/utils/styles'
import { cloneDeep, isNullable } from '@app/utils'

import styles from './styles.module.scss'

import { DEFAULT_TABLE_SCROLL_Y, MIN_TABLE_SCROLL_Y, TABLE_HEADER_HEIGHT_FALLBACK } from './configs'

import type { Column } from '@app/components/base/Table/types'
import type { TableSize } from '@app/hooks/useTableSize/types'
import type { BaseDataSourceType } from '@app/components/ExtendedTable/types'

export const getTableScrollY = (wrapper: HTMLElement | null, wrapperHeight?: number): number => {
  if (wrapperHeight == null) return DEFAULT_TABLE_SCROLL_Y

  const headerEl = wrapper?.querySelector<HTMLElement>('.ant-table-header')
  const headerHeight = headerEl?.getBoundingClientRect().height ?? TABLE_HEADER_HEIGHT_FALLBACK

  return Math.max(Math.floor(wrapperHeight - headerHeight), MIN_TABLE_SCROLL_Y)
}

export const rowClassName = <DataSourceType extends BaseDataSourceType>(record: DataSourceType): string => {
  const BLOCK = 'extendedTable'
  const ELEMENT = 'row'

  return (
    getStylesClassName(styles, {
      block: BLOCK,
      element: ELEMENT,
      modificator: record.rowClassName
    }) ?? ''
  )
}

export const getEllipsisColumns =
  <DataSourceType = unknown,>(rawColumns?: Array<Column<DataSourceType>>, size?: TableSize) =>
    (): Array<Column<DataSourceType>> | undefined => {
      if (isNullable(rawColumns) || size === 'large') return rawColumns

      const columns = cloneDeep(rawColumns)

      for (const column of columns) {
        const { render, customEllipsis } = column

        if (isNullable(customEllipsis)) continue

        column.render = (value: any, record: DataSourceType, index: number): ReactNode => {
          const content = (
            <LntParagraph
              ellipsis={ { rows: 2, tooltip: true } }
              className={ styles.extendedTable__cellWrapper }
            >
              { render?.(value, record, index) ?? value }
            </LntParagraph>
          )

          if (isObject(customEllipsis)) {
            return <RendererWrapper minWidth={ customEllipsis?.minWidth }>{ content }</RendererWrapper>
          }

          return content
        }
      }

      return columns
    }
